import { useEffect, useRef } from "react"
import type { RefObject } from "react"

type EventListeners = {
  [K in keyof GlobalEventHandlersEventMap]?: (
    event: GlobalEventHandlersEventMap[K],
  ) => void
}

/**
 * Subscribes to events for the lifetime of the component, always calling the
 * handler from the latest render.
 *
 * Handlers are new functions on every render, so listing them as dependencies
 * would tear down and re-add every listener each time, while an empty array
 * freezes the first render's closures and leaves them reading stale props.
 * Holding them in a ref gives both: one subscription, current handlers.
 *
 * Pass the target as a ref when it is a DOM node that only exists after mount
 * (the chart SVG), or directly when it always does (`window`). The set of event
 * names is read once, on mount, so keep it fixed across renders.
 */
const useEventListeners = <T extends EventTarget>(
  target: RefObject<T | null> | T,
  listeners: EventListeners,
) => {
  const listenersRef = useRef(listeners)
  listenersRef.current = listeners

  useEffect(() => {
    const node = "current" in target ? target.current : target
    if (!node) return

    const bound = Object.keys(listenersRef.current).map((type) => {
      const handler = (event: Event) =>
        listenersRef.current[type as keyof EventListeners]?.(event as never)

      node.addEventListener(type, handler)
      return { type, handler }
    })

    return () => {
      bound.forEach(({ type, handler }) => node.removeEventListener(type, handler))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}

export default useEventListeners
